import React, { useState, useEffect } from 'react';
import {
    collection,
    addDoc,
    getDocs,
    deleteDoc,
    doc,
    query,
    orderBy,
    updateDoc
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { db, storage } from '../firebaseConfig';

// Componente de Ícone Simplificado
const Icon = ({ name }) => {
    const icons = {
        trash: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        ),
        upload: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        ),
        plus: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v8m0 0v8m0-8h8m-8 0H4" />
        ),
        spinner: (
            <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 0v4" />
        ),
        edit: (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        ),
    };
    const spinClass = name === 'spinner' ? 'animate-spin' : '';
    return (
        <svg className={`w-5 h-5 inline-block ${spinClass}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {icons[name] || null}
        </svg>
    );
};

const F1TeamsManager = () => {
    const [teams, setTeams] = useState([]);
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamLogo, setNewTeamLogo] = useState(null);
    const [editingTeam, setEditingTeam] = useState(null); // Estado para edição
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        fetchTeams();
    }, []);

    const fetchTeams = async () => {
        setFetching(true);
        try {
            const q = query(collection(db, "f1_teams"), orderBy("name"));
            const querySnapshot = await getDocs(q);
            const teamsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTeams(teamsData);
        } catch (error) {
            console.error("Erro ao buscar equipes:", error);
        } finally {
            setFetching(false);
        }
    };

    const handleAddOrUpdateTeam = async (e) => {
        e.preventDefault();
        if (!newTeamName) return;
        setLoading(true);

        try {
            let logoUrl = editingTeam ? editingTeam.logo : '';

            // Se houver uma nova logo selecionada, faz o upload
            if (newTeamLogo) {
                // Se estiver editando e já tiver logo, deleta a antiga (opcional, mas boa prática para não acumular lixo)
                if (editingTeam && editingTeam.logo) {
                    try {
                        const oldLogoRef = ref(storage, editingTeam.logo);
                        await deleteObject(oldLogoRef);
                    } catch (err) {
                        console.warn("Erro ao deletar logo antiga:", err);
                    }
                }

                const storageRef = ref(storage, `f1_teams/${Date.now()}_${newTeamLogo.name}`);
                await uploadBytes(storageRef, newTeamLogo);
                logoUrl = await getDownloadURL(storageRef);
            }

            if (editingTeam) {
                // Atualizar equipe existente
                const teamRef = doc(db, "f1_teams", editingTeam.id);
                await updateDoc(teamRef, {
                    name: newTeamName,
                    logo: logoUrl
                });
            } else {
                // Adicionar nova equipe
                await addDoc(collection(db, "f1_teams"), {
                    name: newTeamName,
                    logo: logoUrl
                });
            }

            resetForm();
            fetchTeams();
        } catch (error) {
            console.error("Erro ao salvar equipe:", error);
            alert("Erro ao salvar equipe: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (team) => {
        setEditingTeam(team);
        setNewTeamName(team.name);
        setNewTeamLogo(null); // Reseta o input de arquivo
    };

    const resetForm = () => {
        setNewTeamName('');
        setNewTeamLogo(null);
        setEditingTeam(null);
    };

    const handleDeleteTeam = async (teamId, logoUrl) => {
        if (!window.confirm("Tem certeza que deseja excluir esta equipe?")) return;

        try {
            await deleteDoc(doc(db, "f1_teams", teamId));

            if (logoUrl) {
                try {
                    const storageRef = ref(storage, logoUrl);
                    await deleteObject(storageRef);
                } catch (storageError) {
                    console.warn("Erro ao deletar imagem (pode já ter sido deletada):", storageError);
                }
            }

            fetchTeams();
        } catch (error) {
            console.error("Erro ao excluir equipe:", error);
            alert("Erro ao excluir equipe.");
        }
    };

    return (
        <div className="bg-gray-800 p-6 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <span className="text-red-500">🏎️</span> Gerenciar Equipes F1
            </h2>

            {/* Form de Adição/Edição */}
            <form onSubmit={handleAddOrUpdateTeam} className="bg-gray-700 p-4 rounded-lg mb-8 border border-gray-600">
                <h3 className="text-lg font-semibold text-gray-200 mb-4">
                    {editingTeam ? 'Editar Equipe' : 'Nova Equipe'}
                </h3>
                <div className="flex flex-col md:flex-row gap-4 items-end">
                    <div className="flex-1 w-full">
                        <label className="block text-gray-400 mb-1 text-sm">Nome da Equipe</label>
                        <input
                            type="text"
                            value={newTeamName}
                            onChange={(e) => setNewTeamName(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded focus:outline-none focus:border-teal-500 text-white"
                            placeholder="Ex: Ferrari"
                        />
                    </div>
                    <div className="w-full md:w-auto">
                        <label className="block text-gray-400 mb-1 text-sm">Logo (Opcional)</label>
                        <label className="flex items-center justify-center px-4 py-2 bg-gray-600 border border-gray-500 rounded cursor-pointer hover:bg-gray-500 transition-colors">
                            <Icon name="upload" />
                            <span className="ml-2 text-sm text-gray-300 truncate max-w-[150px]">
                                {newTeamLogo ? newTeamLogo.name : "Escolher Imagem"}
                            </span>
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => setNewTeamLogo(e.target.files[0])}
                            />
                        </label>
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        {editingTeam && (
                            <button
                                type="button"
                                onClick={resetForm}
                                className="flex-1 md:flex-none bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded transition-colors"
                            >
                                Cancelar
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={loading || !newTeamName}
                            className="flex-1 md:flex-none bg-teal-500 hover:bg-teal-600 text-white font-bold py-2 px-6 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {loading ? <Icon name="spinner" /> : (
                                <>
                                    <Icon name={editingTeam ? "upload" : "plus"} />
                                    <span className="ml-1">{editingTeam ? "Atualizar" : "Adicionar"}</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>

            {/* Lista de Equipes */}
            <div className="space-y-2">
                {fetching ? (
                    <p className="text-gray-400 text-center py-4">Carregando equipes...</p>
                ) : teams.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Nenhuma equipe cadastrada.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {teams.map((team) => (
                            <div key={team.id} className="bg-gray-700 p-4 rounded-lg flex items-center justify-between border border-gray-600 hover:border-gray-500 transition-colors">
                                <div className="flex items-center gap-3">
                                    {team.logo ? (
                                        <img src={team.logo} alt={team.name} className="w-10 h-10 object-contain" />
                                    ) : (
                                        <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center border border-gray-500">
                                            <span className="text-xs text-gray-400">N/A</span>
                                        </div>
                                    )}
                                    <span className="text-white font-medium">{team.name}</span>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEditClick(team)}
                                        className="text-blue-400 hover:text-blue-300 p-2 hover:bg-blue-400/10 rounded transition-colors"
                                        title="Editar Equipe"
                                    >
                                        <Icon name="edit" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteTeam(team.id, team.logo)}
                                        className="text-red-400 hover:text-red-300 p-2 hover:bg-red-400/10 rounded transition-colors"
                                        title="Excluir Equipe"
                                    >
                                        <Icon name="trash" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div >
    );
};

export default F1TeamsManager;
